import type { ApiClient } from '@momonos/api-client'
import { Button, Card, useToast } from '@momonos/ui'
import { Sparkles } from 'lucide-react'
import { useState } from 'react'

/**
 * FR-AIA — ask questions of your own numbers.
 *
 * Answers come from fixed query templates over rollups; suggestions become
 * drafts in the Campaigns queue. The assistant cannot send anything — the
 * footer says so because trust needs the limits stated where the tool is.
 */

interface Answer {
  intent: string
  answer: string
  rows?: Array<Record<string, string | number | null>>
  draftCampaignId?: string
}

const SUGGESTIONS = [
  "Which customers haven't returned in 21 days?",
  'What should we promote this weekend?',
  'When are we busiest?',
  'Which campaign has the best ROI?',
  'What is our points liability?',
  'Send 15% off to customers inactive for 21 days',
]

export function AssistantScreen({ api }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [question, setQuestion] = useState('')
  const [history, setHistory] = useState<Array<{ id: string; question: string; result: Answer }>>(
    [],
  )
  const [busy, setBusy] = useState(false)

  async function ask(text: string) {
    if (text.trim().length < 3) return
    setBusy(true)
    try {
      const result = await api.post<Answer>('/v1/admin/assistant', { question: text })
      setHistory((current) => [{ id: crypto.randomUUID(), question: text, result }, ...current])
      setQuestion('')
    } catch {
      show('Could not answer that', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-bold text-2xl text-content">
          <Sparkles aria-hidden="true" className="size-6 text-momo-yellow" /> Marketing assistant
        </h1>
        <p className="text-content-muted text-sm">
          Reads your rollups; drafts campaigns for approval. It cannot send messages or spend
          anything — approval stays with you, in Campaigns.
        </p>
      </div>

      <div className="flex gap-2">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void ask(question)
          }}
          placeholder="Ask about customers, sales, campaigns…"
          className="h-12 flex-1 rounded-lg border border-surface-border bg-surface-raised px-3 text-content"
        />
        <Button isLoading={busy} onClick={() => ask(question)}>
          Ask
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => ask(suggestion)}
            className="rounded-full border border-surface-border px-3 py-1.5 text-content-secondary text-xs transition-colors hover:border-momo-yellow hover:text-momo-yellow"
          >
            {suggestion}
          </button>
        ))}
      </div>

      {history.map((entry) => (
        <Card key={entry.id} className="space-y-2">
          <p className="text-content-muted text-sm">“{entry.question}”</p>
          <p className="text-content">{entry.result.answer}</p>

          {entry.result.draftCampaignId && (
            <p className="rounded-lg bg-momo-yellow/10 px-3 py-2 text-momo-yellow text-sm">
              Draft created — review it under Campaigns before anything sends.
            </p>
          )}

          {entry.result.rows && entry.result.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-content-muted">
                    {Object.keys(entry.result.rows[0] ?? {}).map((column) => (
                      <th key={column} className="py-1 pr-4 font-medium capitalize">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entry.result.rows.map((row) => (
                    <tr key={JSON.stringify(row)} className="border-surface-border border-t">
                      {Object.values(row).map((value, columnIndex) => (
                        <td
                          key={`${JSON.stringify(row)}-${Object.keys(row)[columnIndex]}`}
                          className="py-1.5 pr-4 text-content-secondary tabular"
                        >
                          {value ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}
    </div>
  )
}
